import { InternalLogicError } from '@/errors/InternalLogicError';
import { isSearchTreeLeafNode, SearchTreeNode } from '@/types';
import { TemplatePlaceholderWithNodeAndFilterContext } from '@/pages/SearchTemplatePage/types';
import {
  findPlaceholdersInSearchTree,
  getPlaceholderFiltersInLeafNode,
  placeholderFieldHash,
} from '@/pages/SearchTemplatePage/util';

/**
 * Whenever the search tree is modified, we want to update the placeholders
 * data. This also includes adding positions to any new placeholders and
 * reindexing if placeholders are removed.
 *
 * We can't just replace it wholesale though - that would clobber any changes
 * made by the user, because we only update the searchTree with the placeholders
 * when clicking save and preview (since its very expensive).
 *
 * There are 3 possible scenarios this gets called in:
 *
 * 1. The tree changes but no placeholders change.
 *
 * 2. The tree changes because a placeholder was added.
 *
 * 3. The tree changes because a placeholder was removed.
 */
export function regeneratePlaceholdersData(
  oldPlaceholdersData: Record<string, TemplatePlaceholderWithNodeAndFilterContext>,
  searchTree: SearchTreeNode | undefined
) {
  // We could probably make some assumptions (e.g. only one placeholder
  // added/removed per call) and optimise around that, but its safer not to -
  // and this function doesn't get called frequently anyway.

  const placeholdersDataInTree = findPlaceholdersInSearchTree(searchTree);

  // Clone the old placeholders data so we can more easily work with it
  const newPlaceholdersData = structuredClone(oldPlaceholdersData);

  // Add placeholders that are in tree but not the oldPlaceholdersData
  for (const [key, data] of Object.entries(placeholdersDataInTree)) {
    if (!(key in newPlaceholdersData)) {
      newPlaceholdersData[key] = structuredClone(data);
    }
  }

  // Remove placeholders that in olderPlaceholdersData but not in tree
  Object.keys(oldPlaceholdersData).forEach((key) => {
    if (!(key in placeholdersDataInTree)) {
      delete newPlaceholdersData[key];
    }
  });

  // Next we want to ensure the correct positions/order of placeholders are applied

  // First what keys are kept and what are new
  const keptKeys = Object.keys(oldPlaceholdersData).filter((k) => k in newPlaceholdersData);
  const newKeysSet = new Set(
    Object.keys(newPlaceholdersData).filter((k) => !(k in oldPlaceholdersData))
  );

  // Then we want to sort the kept keys by their position
  // Ideally they should all have defined positions, but if they don't we want
  // put them at the end
  const keptKeysSorted = [...keptKeys].sort((a, b) => {
    const posA = newPlaceholdersData[a].placeholder.position;
    const posB = newPlaceholdersData[b].placeholder.position;
    const aUndef = posA === undefined;
    const bUndef = posB === undefined;
    if (aUndef && bUndef) return 0;
    if (aUndef) return 1;
    if (bUndef) return -1;
    return (posA as number) - (posB as number);
  });

  // Sort the new keys in DFS order. Generally there will only be a single new
  // key, but we don't have to make that assumption (as above)
  const dfsKeys = getPlaceholderKeysInDepthFirstOrder(searchTree);
  const newKeysOrdered = dfsKeys.filter((k) => newKeysSet.has(k));

  // Then we want to combine the kept and new keys in the correct order
  // New keys are appended to the end.
  const canonicalOrder = keptKeysSorted.concat(newKeysOrdered);

  // Finally we can make use of an existing util function to apply the positions
  return applyPlaceholderPositionsFromOrder(newPlaceholdersData, canonicalOrder);
}

/**
 * Returns placeholder hash keys in depth-first order. Same traversal as
 * findPlaceholdersInSearchNode; at each leaf, keys follow the order returned
 * by getPlaceholderFiltersInLeafNode.
 */
export function getPlaceholderKeysInDepthFirstOrder(
  searchTree: SearchTreeNode | undefined
): string[] {
  const keys: string[] = [];
  function walk(node: SearchTreeNode | undefined): void {
    if (!node) return;
    if (isSearchTreeLeafNode(node)) {
      const leafNodeId = node._leafNodeId;
      if (!leafNodeId) {
        throw new InternalLogicError(
          '_leafNodeId not set on leaf node. Did not expect getPlaceholderKeysInDepthFirstOrder to be called before these were populated.'
        );
      }
      const placeholdersInNode = getPlaceholderFiltersInLeafNode(node);
      for (const x of placeholdersInNode) {
        keys.push(placeholderFieldHash(leafNodeId, x.filterNameAndField));
      }
    } else {
      for (const child of node.nodes) {
        walk(child);
      }
    }
  }
  walk(searchTree);
  return keys;
}

/**
 * Merges previous placeholder key order with the current tree: keeps keys that
 * still exist (in previous order), appends new keys in DFS order. Removed
 * placeholders are dropped. Used when the tree changes so ARIA labels stay
 * stable; initial order is set from position via getPlaceholderKeysInPositionOrder.
 */
export function mergePlaceholderKeyOrder(
  previousOrder: string[],
  newSearchTree: SearchTreeNode | undefined
): string[] {
  const newKeys = getPlaceholderKeysInDepthFirstOrder(newSearchTree);
  const newSet = new Set(newKeys);
  const kept = previousOrder.filter((k) => newSet.has(k));
  const added = newKeys.filter((k) => !previousOrder.includes(k));
  return kept.concat(added);
}

const ARIA_TITLE_SUFFIXES = [' A', ' B', ' C', ' D', ' E', ' F', ' G', ' H', ' I', ' J'] as const;

/**
 * Builds a map of placeholder key to ARIA title (for screen readers). When multiple
 * placeholders share the same filterNameAndField (e.g. two "age" filters), appends
 * " A", " B", " C" etc. by order in placeholderKeysInAddedOrder. Keys not in
 * placeholdersData are skipped. Does not mutate stored placeholder.title.
 */
export function getPlaceholderAriaTitlesByKey(
  placeholderKeysInAddedOrder: string[],
  placeholdersData: Record<string, TemplatePlaceholderWithNodeAndFilterContext>
): Record<string, string> {
  const result: Record<string, string> = {};
  const keysByFilterAndField = new Map<string, string[]>();
  for (const key of placeholderKeysInAddedOrder) {
    if (!(key in placeholdersData)) continue;
    const colonIdx = key.indexOf(':');
    const filterNameAndField = colonIdx >= 0 ? key.slice(colonIdx + 1) : key;
    const list = keysByFilterAndField.get(filterNameAndField) ?? [];
    list.push(key);
    keysByFilterAndField.set(filterNameAndField, list);
  }
  for (const keys of keysByFilterAndField.values()) {
    const suffixList = keys.length > 1 ? keys : [];
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const data = placeholdersData[key];
      const suffix = suffixList.length > 0 ? (ARIA_TITLE_SUFFIXES[i] ?? ` ${i + 1}`) : '';
      result[key] = data.placeholder.title + suffix;
    }
  }
  return result;
}

/**
 * Applies placeholder positions from the ordered keys to the placeholders data.
 * Also reindexes the positions to be sequential.
 *
 * @param placeholdersData - The placeholders data to apply the positions to.
 *
 * @param orderedKeys - the canonical order (e.g. placeholderKeysInAddedOrder);
 * keys that aren’t in placeholdersData are ignored.
 *
 * @returns a new record with the same keys and values, with each entry’s
 * placeholder.position set to that key’s index in orderedKeys
 */
export function applyPlaceholderPositionsFromOrder(
  placeholdersData: Record<string, TemplatePlaceholderWithNodeAndFilterContext>,
  orderedKeys: string[]
): Record<string, TemplatePlaceholderWithNodeAndFilterContext> {
  const result: Record<string, TemplatePlaceholderWithNodeAndFilterContext> = {};
  for (const key of Object.keys(placeholdersData)) {
    const data = placeholdersData[key];
    const index = orderedKeys.indexOf(key);
    result[key] = {
      ...data,
      placeholder: {
        ...data.placeholder,
        ...(index >= 0 && { position: index }),
      },
    };
  }
  return result;
}
